import { Component, Input, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-select-check-all',
  templateUrl: './select-check-all.component.html',
  styleUrls: ['./select-check-all.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class SelectCheckAllComponent {
  @Input()
  model: UntypedFormControl = new UntypedFormControl();
  @Input() values: any[] = [];
  @Input() text = 'Select All';
  /**
   * When true: check merges `values` into current selection; uncheck removes `values`,
   * or if `values` is empty (everything visible already selected) removes `visibleOptionIds`.
   */
  @Input() mergeWithSelection = false;
  /** All option ids currently shown in the panel (same order as rows). Used when `values` is empty after a full select-all. */
  @Input() visibleOptionIds: any[] = [];

  constructor() {}

  /** Batch used for checked / indeterminate when merge + values became empty. */
  private mergePanelBatch(): any[] {
    if (!this.mergeWithSelection) {
      return this.values;
    }
    return this.values.length ? this.values : this.visibleOptionIds;
  }

  isChecked(): boolean {
    if (this.mergeWithSelection) {
      const batch = this.mergePanelBatch();
      if (!batch?.length || !this.model.value) {
        return false;
      }
      const selected = new Set(
        (this.model.value as any[]).map((x) => String(x))
      );
      return batch.every((v) => selected.has(String(v)));
    }
    if (!this.model.value || !this.values?.length) {
      return false;
    }
    const val = this.model.value as any[] | undefined;
    return !!val && val.length === this.values.length;
  }

  isIndeterminate(): boolean {
    if (this.mergeWithSelection) {
      const batch = this.mergePanelBatch();
      if (!batch?.length) {
        return false;
      }
      const selected = new Set(
        ((this.model.value as any[]) || []).map((x) => String(x))
      );
      const hit = batch.filter((v) => selected.has(String(v))).length;
      return hit > 0 && hit < batch.length;
    }
    if (!this.values?.length) {
      return false;
    }
    return (
      !!this.model.value &&
      this.model.value.length > 0 &&
      this.model.value.length < this.values.length
    );
  }

  toggleSelection(change: MatCheckboxChange): void {
    if (this.mergeWithSelection) {
      const selected = new Set(
        ((this.model.value as any[]) || []).map((x) => String(x))
      );
      if (change.checked) {
        if (!this.values?.length) {
          return;
        }
        const merged: string[] = [...selected];
        for (const v of this.values) {
          const s = String(v);
          if (!selected.has(s)) {
            merged.push(s);
            selected.add(s);
          }
        }
        this.model.setValue(merged, { emitEvent: true });
      } else {
        const removeSource = this.values.length
          ? this.values
          : this.visibleOptionIds;
        const remove = new Set(removeSource.map((v) => String(v)));
        if (!remove.size) {
          return;
        }
        this.model.setValue(
          ((this.model.value as any[]) || []).filter(
            (id: any) => !remove.has(String(id))
          ),
          { emitEvent: true }
        );
      }
      return;
    }
    if (!this.values?.length) {
      return;
    }
    if (change.checked) {
      this.model.setValue(this.values, { emitEvent: true });
    } else {
      this.model.setValue([], { emitEvent: true });
    }
  }
}
